import type { Asset, MediaKind } from "@studio/shared";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MediaThumb } from "./MediaThumb";
import { Button, Segmented, Sheet } from "./ui";

const ACCEPT: Record<MediaKind, string> = {
  image: "image/*",
  video: "video/*",
  audio: "audio/*",
  document: "application/pdf",
};

/** Pick something for a slot: straight from the device (Photos or camera on a phone) or from the library. */
export function SlotPicker({
  open,
  onOpenChange,
  title,
  description,
  kinds,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  kinds: MediaKind[];
  onPick: (asset: Asset) => void;
}) {
  const [kind, setKind] = useState<MediaKind>(kinds[0] ?? "image");
  const [progress, setProgress] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const library = useInfiniteQuery({
    queryKey: ["assets", { kind }],
    queryFn: ({ pageParam }) => api.assets({ kind, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    enabled: open,
  });
  const items = library.data?.pages.flatMap((p) => p.items) ?? [];

  const uploadFiles = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setProgress(0);
    try {
      const asset = await api.upload(file, setProgress);
      if (!kinds.includes(asset.kind)) {
        toast.error(`This slot takes ${kinds.join(" or ")}, not ${asset.kind}.`);
        return;
      }
      onPick(asset);
      onOpenChange(false);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setProgress(null);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={title} description={description} wide>
      <input
        ref={input}
        type="file"
        className="hidden"
        accept={kinds.map((k) => ACCEPT[k]).join(",")}
        onChange={(e) => uploadFiles(e.target.files)}
      />
      <Button variant="outline" className="w-full" onClick={() => input.current?.click()} disabled={progress !== null}>
        <Upload className="size-4" />
        {progress !== null ? `Uploading… ${Math.round(progress * 100)}%` : "Upload from this device"}
      </Button>
      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="text-sm font-medium">From your library</span>
        {kinds.length > 1 ? (
          <Segmented
            size="sm"
            value={kind}
            onChange={setKind}
            options={kinds.map((k) => ({ value: k, label: k === "image" ? "Images" : k === "video" ? "Clips" : "Sounds" }))}
          />
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-mute">
          {library.isLoading ? "Loading…" : "Nothing here yet. Upload a file or create something first."}
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
          {items.map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => {
                onPick(asset);
                onOpenChange(false);
              }}
              className="group rounded-[var(--radius-media)] outline-none focus-visible:ring-2 focus-visible:ring-film"
            >
              <MediaThumb asset={asset} className="aspect-square transition group-hover:brightness-110" />
            </button>
          ))}
        </div>
      )}
      {library.hasNextPage ? (
        <Button variant="quiet" className="mt-3 w-full" onClick={() => library.fetchNextPage()}>
          Show more
        </Button>
      ) : null}
    </Sheet>
  );
}
